import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Upload, X, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title_en: '',
    title_fr: '',
    title_he: '',
    title_ru: '',
    type: 'apartment',
    city: 'Netanya',
    price: '',
    surface: '',
    rooms: '',
    bathrooms: '',
    description_en: '',
    description_fr: '',
    description_he: '',
    description_ru: '',
    status: 'sale',
    featured: false,
    virtual_tour: '',
    images: []
  });

  useEffect(() => {
    if (id) {
      fetchProperty();
    }
  }, [id]);

  const fetchProperty = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Erreur de chargement de la propriété');
      navigate('/admin/dashboard');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (formData.images.length + files.length > 15) {
      toast.error('Maximum 15 photos par propriété');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData({ ...formData, images: [...formData.images, ...uploadedUrls] });
      toast.success(`${uploadedUrls.length} photo(s) uploadée(s) !`);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      if (id) {
        // Update
        await axios.put(`${BACKEND_URL}/api/properties/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Propriété mise à jour !');
      } else {
        // Create
        await axios.post(`${BACKEND_URL}/api/properties`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Propriété créée !');
      }
      
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-xl font-bold text-[#1a365d]">
              {id ? 'Modifier la Propriété' : 'Nouvelle Propriété'}
            </h1>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Images */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#1a365d] mb-6">
              Photos ({formData.images.length}/15)
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {formData.images.map((img, index) => (
                <div key={index} className="relative group">
                  <img src={img} alt={`Photo ${index + 1}`} className="w-full h-32 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploading || formData.images.length >= 15}
                className="mb-2"
              />
              <p className="text-sm text-gray-500">
                {uploading ? 'Upload en cours...' : 'Choisissez jusqu\'à 15 photos (JPG, PNG)'}
              </p>
            </div>
          </Card>

          {/* Titres */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Titres (4 langues)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Titre (English) *</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  required
                  placeholder="Luxury Penthouse with Sea View"
                />
              </div>
              <div>
                <Label>Titre (Français) *</Label>
                <Input
                  value={formData.title_fr}
                  onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                  required
                  placeholder="Penthouse de luxe avec vue mer"
                />
              </div>
              <div>
                <Label>Titre (עברית) *</Label>
                <Input
                  value={formData.title_he}
                  onChange={(e) => setFormData({ ...formData, title_he: e.target.value })}
                  required
                  placeholder="פנטהאוז יוקרתי עם נוף לים"
                />
              </div>
              <div>
                <Label>Titre (Русский) *</Label>
                <Input
                  value={formData.title_ru}
                  onChange={(e) => setFormData({ ...formData, title_ru: e.target.value })}
                  required
                  placeholder="Роскошный пентхаус с видом на море"
                />
              </div>
            </div>
          </Card>

          {/* Détails */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Détails</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="apartment">Appartement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ville *</Label>
                <Select value={formData.city} onValueChange={(value) => setFormData({ ...formData, city: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Netanya">Netanya</SelectItem>
                    <SelectItem value="Jerusalem">Jerusalem</SelectItem>
                    <SelectItem value="Tel Aviv">Tel Aviv</SelectItem>
                    <SelectItem value="Raanana">Raanana</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Statut *</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">À vendre</SelectItem>
                    <SelectItem value="rent">À louer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Prix (₪) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="12500000"
                />
              </div>

              <div>
                <Label>Surface (m²) *</Label>
                <Input
                  type="number"
                  value={formData.surface}
                  onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                  required
                  placeholder="350"
                />
              </div>

              <div>
                <Label>Chambres *</Label>
                <Input
                  type="number"
                  value={formData.rooms}
                  onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                  required
                  placeholder="6"
                />
              </div>

              <div>
                <Label>Salles de bain *</Label>
                <Input
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  required
                  placeholder="4"
                />
              </div>

              <div>
                <Label>Lien Visite Virtuelle</Label>
                <Input
                  value={formData.virtual_tour}
                  onChange={(e) => setFormData({ ...formData, virtual_tour: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label>Propriété en vedette</Label>
              </div>
            </div>
          </Card>

          {/* Descriptions */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Descriptions</h2>
            <div className="space-y-4">
              <div>
                <Label>Description (English) *</Label>
                <Textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  rows={3}
                  required
                  placeholder="Luxurious penthouse with panoramic Mediterranean sea view..."
                />
              </div>
              <div>
                <Label>Description (Français) *</Label>
                <Textarea
                  value={formData.description_fr}
                  onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                  rows={3}
                  required
                  placeholder="Penthouse luxueux avec vue panoramique sur la Méditerranée..."
                />
              </div>
              <div>
                <Label>Description (עברית) *</Label>
                <Textarea
                  value={formData.description_he}
                  onChange={(e) => setFormData({ ...formData, description_he: e.target.value })}
                  rows={3}
                  required
                  placeholder="פנטהאוז יוקרתי עם נוף פנורמי לים התיכון..."
                />
              </div>
              <div>
                <Label>Description (Русский) *</Label>
                <Textarea
                  value={formData.description_ru}
                  onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })}
                  rows={3}
                  required
                  placeholder="Роскошный пентхаус с панорамным видом на Средиземное море..."
                />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/dashboard')}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#1a365d] hover:bg-[#d4af37] px-8">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Sauvegarde...' : id ? 'Mettre à jour' : 'Créer la Propriété'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default PropertyForm;
